import { cloneElement, isValidElement, useEffect, useMemo, useRef } from 'react';
import { pick } from 'lodash-es';

import {
  getScrollObserverRootElement,
  SCROLL_OBSERVER_ENTRY_ID,
  useScrollObserverRootContext,
  useScrollObserverStore,
} from '@kc-console/context';
import { getUuid } from '@kc-console/core';

import type { ReactElement } from 'react';
import type { PropsWithRefObject } from '@kc-console/common-ui';
import type { ScrollObserverEntryId, ScrollObserverEntryIdProps } from '@kc-console/context';

type ScrollObserverEntryWrapperProps = {
  targetComponent: ReactElement;
  entryId?: ScrollObserverEntryId;
  observerCallback?: (entry: IntersectionObserverEntry) => void;
};

const ScrollObserverEntryWrapper = (props: ScrollObserverEntryWrapperProps) => {
  const { targetComponent, entryId: entryIdProp, observerCallback } = props;

  const uuid = useMemo(() => getUuid(), []);
  const entryId = useMemo(() => entryIdProp || uuid, [entryIdProp, uuid]);

  const addEntry = useScrollObserverStore(state => state.addEntry);
  const updateEntry = useScrollObserverStore(state => state.updateEntry);
  const removeEntry = useScrollObserverStore(state => state.removeEntry);
  const { rootId, rootMargin, threshold } = useScrollObserverRootContext();

  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (observerCallback) {
          observerCallback(entry);
        } else {
          updateEntry({
            entryId,
            rootId,
            intersectionRatio: entry.intersectionRatio,
            isIntersecting: entry.isIntersecting,
            time: entry.time,
            boundingClientRect: pick(entry.boundingClientRect, ['height', 'top']),
            intersectionRect: pick(entry.intersectionRect, ['height', 'top']),
            rootBounds: pick(entry.rootBounds || { height: 0, top: 0 }, ['height', 'top']),
          });
        }
      },
      {
        root: getScrollObserverRootElement(rootId),
        rootMargin,
        threshold,
      },
    );

    const entryElement = ref.current;

    if (entryElement) {
      observer.observe(entryElement);
      addEntry({
        entryId,
        rootId,
        intersectionRatio: 0,
        isIntersecting: false,
        time: 0,
        boundingClientRect: { top: 0, height: 0 },
        intersectionRect: { top: 0, height: 0 },
        rootBounds: { top: 0, height: 0 },
      });
    }

    return () => {
      if (entryElement) {
        observer.disconnect();
        removeEntry({ entryId, rootId });
      }
    };
  }, [
    addEntry,
    entryId,
    observerCallback,
    removeEntry,
    rootId,
    rootMargin,
    threshold,
    updateEntry,
  ]);

  const element = isValidElement<
    PropsWithRefObject<typeof targetComponent> & ScrollObserverEntryIdProps
  >(targetComponent)
    ? cloneElement(targetComponent, {
        ...targetComponent.props,
        [SCROLL_OBSERVER_ENTRY_ID]: entryId,
        ref,
      })
    : targetComponent;

  return element;
};

export default ScrollObserverEntryWrapper;
